'use client';

import {
  useCallback,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent
} from 'react';
import { Box, Button, Link, keyframes, useTheme } from '@mui/material';
import {
  Body,
  Subheading,
  Base
} from '@natomalabs/natomui/src/components/Typography';
import { ArrowUpRightIcon, SearchIcon } from '@natomalabs/natomui/src/icons';

/* ------------------------------------------------------------------ */
/*  Keyframes                                                          */
/* ------------------------------------------------------------------ */

const pulse = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50% { opacity: 0.25; transform: scale(1.04); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const RING_SIZES = [140, 200, 268];
const ICON_BOX = 80;
const ICON_SIZE = 36;

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

/** Normalized mouse offset from center of an element, clamped to [-1, 1]. */
function useMouseTilt(clamp = 12) {
  const ref = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const rafId = useRef<number>(0);

  const onMove = useCallback(
    (e: ReactMouseEvent) => {
      const el = ref.current;
      if (!el) return;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const nx = Math.max(
          -1,
          Math.min(1, (e.clientX - cx) / (rect.width / 2))
        );
        const ny = Math.max(
          -1,
          Math.min(1, (e.clientY - cy) / (rect.height / 2))
        );
        setTilt({ x: nx * clamp, y: -ny * clamp });
      });
    },
    [clamp]
  );

  const onLeave = useCallback(() => {
    cancelAnimationFrame(rafId.current);
    setTilt({ x: 0, y: 0 });
  }, []);

  return { ref, tilt, onMove, onLeave };
}

/* ------------------------------------------------------------------ */
/*  Animated hero graphic                                              */
/* ------------------------------------------------------------------ */

function NotFoundHero({ tilt }: { tilt: { x: number; y: number } }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const brandColor = isDark ? '#7bf1a8' : '#00a63e';
  const ringColor = isDark
    ? 'rgba(123, 241, 168, 0.10)'
    : 'rgba(0, 166, 62, 0.06)';
  const dotColor = isDark
    ? 'rgba(123, 241, 168, 0.4)'
    : 'rgba(0, 166, 62, 0.25)';

  const lightX = 50 + tilt.x * 2;
  const lightY = 50 - tilt.y * 2;

  const iconBoxBg = isDark
    ? `radial-gradient(circle at ${lightX}% ${lightY}%, rgba(123, 241, 168, 0.12) 0%, rgba(123, 241, 168, 0.03) 70%, transparent 100%)`
    : `radial-gradient(circle at ${lightX}% ${lightY}%, rgba(0, 166, 62, 0.08) 0%, rgba(0, 166, 62, 0.02) 70%, transparent 100%)`;
  const iconBoxBorder = isDark
    ? '1px solid rgba(123, 241, 168, 0.15)'
    : '1px solid rgba(0, 166, 62, 0.10)';
  const iconBoxShadow = isDark
    ? `0 0 32px rgba(123, 241, 168, ${0.05 + Math.abs(tilt.x / 12) * 0.05}), inset 0 1px 0 rgba(255,255,255,0.03)`
    : `0 0 32px rgba(0, 166, 62, ${0.03 + Math.abs(tilt.x / 12) * 0.03}), inset 0 1px 0 rgba(255,255,255,0.4)`;

  const parallax = (depth: number) =>
    `translate(${-tilt.x * depth}px, ${tilt.y * depth}px)`;

  return (
    <Box
      sx={{
        position: 'relative',
        width: RING_SIZES[2],
        height: RING_SIZES[2],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        animation: `${fadeIn} 0.6s ease-out both`
      }}
    >
      {/* Concentric pulsing rings */}
      {RING_SIZES.map((size, i) => (
        <Box
          key={size}
          sx={{
            position: 'absolute',
            width: size,
            height: size,
            borderRadius: '50%',
            border: `1px solid ${ringColor}`,
            animation: `${pulse} ${3 + i * 0.8}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`,
            transform: parallax(0.3 + i * 0.25),
            transition: 'transform 0.3s ease-out'
          }}
        />
      ))}

      {/* Orbiting dots — outer ring */}
      <Box
        sx={{
          position: 'absolute',
          width: RING_SIZES[2],
          height: RING_SIZES[2],
          animation: `${rotate} 24s linear infinite`,
          transform: parallax(0.6),
          transition: 'transform 0.3s ease-out'
        }}
      >
        {[0, 120, 240].map((deg) => (
          <Box
            key={deg}
            sx={{
              position: 'absolute',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: dotColor,
              top: '50%',
              left: '50%',
              transform: `rotate(${deg}deg) translateX(${RING_SIZES[2] / 2}px) translate(-50%, -50%)`
            }}
          />
        ))}
      </Box>

      {/* Counter-rotating dots — middle ring */}
      <Box
        sx={{
          position: 'absolute',
          width: RING_SIZES[1],
          height: RING_SIZES[1],
          animation: `${rotate} 16s linear infinite reverse`,
          transform: parallax(0.4),
          transition: 'transform 0.3s ease-out'
        }}
      >
        {[30, 150, 270].map((deg) => (
          <Box
            key={deg}
            sx={{
              position: 'absolute',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: dotColor,
              top: '50%',
              left: '50%',
              transform: `rotate(${deg}deg) translateX(${RING_SIZES[1] / 2}px) translate(-50%, -50%)`
            }}
          />
        ))}
      </Box>

      {/* Center icon in a glowing rounded box */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: ICON_BOX,
          height: ICON_BOX,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '18px',
          background: iconBoxBg,
          border: iconBoxBorder,
          boxShadow: iconBoxShadow,
          transform: `perspective(400px) rotateY(${tilt.x * 0.8}deg) rotateX(${tilt.y * 0.8}deg)`,
          transition:
            'transform 0.2s ease-out, box-shadow 0.3s ease-out, background 0.3s ease-out',
          '& svg': {
            width: ICON_SIZE,
            height: ICON_SIZE,
            color: brandColor
          }
        }}
      >
        <SearchIcon />
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function NotFoundState({
  homeHref,
  homeLabel = 'Go to Home'
}: {
  homeHref: string;
  homeLabel?: string;
}) {
  const { ref, tilt, onMove, onLeave } = useMouseTilt();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const brandColorDim = isDark
    ? 'rgba(123, 241, 168, 0.12)'
    : 'rgba(0, 166, 62, 0.08)';

  return (
    <Box
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        gap: theme.spacing(3),
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        // Soft radial glow behind center
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${brandColorDim} 0%, transparent 70%)`,
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -60%) translate(${-tilt.x * 0.5}px, ${tilt.y * 0.5}px)`,
          transition: 'transform 0.3s ease-out',
          pointerEvents: 'none'
        }
      })}
      data-testid="NotFoundState-root"
    >
      <NotFoundHero tilt={tilt} />

      {/* Text */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(1),
          maxWidth: 360,
          animation: `${fadeIn} 0.6s ease-out 0.15s both`
        })}
      >
        <Subheading lineHeight={'32px'} textAlign={'center'}>
          Page not found
        </Subheading>
        <Base lineHeight={'20px'} textAlign={'center'} sx={{ opacity: 0.7 }}>
          The page you requested could not be found.
        </Base>
      </Box>

      {/* Home button */}
      <Box sx={{ animation: `${fadeIn} 0.6s ease-out 0.3s both` }}>
        <Button
          href={homeHref}
          LinkComponent={Link}
          variant="primary"
          endIcon={<ArrowUpRightIcon />}
        >
          <Body lineHeight={'20px'}>{homeLabel}</Body>
        </Button>
      </Box>
    </Box>
  );
}
